// What the assessment screen does with Salesforce's copy of an assessment, and
// what it sends back. Kept free of React so every decision can be tested alone.
//
// Three parts are saved, each independently: the ANALYSIS (the bundle read from
// the household's statements, store key `assessment.holdings`), the SKETCHES
// (the whiteboards, `assessment.sketches`) and the META (the advisor's choices:
// which sections make the meeting and the packet, which years the goal sheet
// reads from, and the version marked reviewed; `assessment.meta`). Salesforce's
// endpoint treats a part left out of a save as "leave it alone", so saving one
// never touches the others.
//
// Until this existed, both lived only in the browser's local storage, and the
// store's own comment said so: "local is the only copy there is". Anything a
// planner or advisor produced before this shipped is on their machine and
// nowhere else, which is why an unsynced local copy is pushed up on open
// rather than overwritten by an empty Salesforce record.

// The Long Text Area maximum, which is what both Salesforce fields are.
export const ASSESSMENT_LIMIT = 131072;

export const PARTS = Object.freeze({
  analysis: { store: 'holdings', field: 'assessmentAnalysis', label: 'The analysis', plural: false },
  sketches: { store: 'sketches', field: 'assessmentSketches', label: 'The sketches', plural: true },
  meta: { store: 'meta', field: 'assessmentMeta', label: 'The choices', plural: true },
});

const isEmptyValue = v =>
  v == null || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

// The string that would be saved for each part, or null when there is nothing
// to save. Serialised the same way on both sides so they can be compared.
export function serializeParts(assessment) {
  const out = {};
  for (const [part, { store }] of Object.entries(PARTS)) {
    const value = assessment ? assessment[store] : null;
    out[part] = isEmptyValue(value) ? null : JSON.stringify(value);
  }
  return out;
}

// Salesforce's answer, as the same strings plus parsed values. A value that
// does not parse is treated as absent rather than breaking the page.
export function parseRemote(body) {
  const strings = {};
  const values = {};
  for (const [part, { field }] of Object.entries(PARTS)) {
    const raw = body ? body[field] : null;
    strings[part] = null;
    values[part] = null;
    if (typeof raw === 'string' && raw !== '') {
      try {
        const parsed = JSON.parse(raw);
        if (!isEmptyValue(parsed)) {
          values[part] = parsed;
          strings[part] = JSON.stringify(parsed);
        }
      } catch {
        // Unreadable: behave as though nothing is saved, and let a local copy
        // replace it.
      }
    }
  }
  return { strings, values, profileExists: body ? body.profileExists !== false : false };
}

// What to do with each part once Salesforce has answered.
//   adopt   - take Salesforce's copy into the page
//   push    - send the local copy up
//   waiting - there is a local copy, but no Financial Profile to put it on yet
//   none    - nothing to do
//
// `pending` marks parts changed on this device and not yet saved; those win,
// because they are the newest thing anyone has. Otherwise Salesforce wins,
// because it is the shared copy. A local copy with nothing in Salesforce is
// pushed, which is how work from before this feature reaches Salesforce.
// A read-only viewer only ever adopts.
export function reconcile({ local, remote, pending = {}, profileExists = true, readOnly = false }) {
  const actions = {};
  for (const part of Object.keys(PARTS)) {
    const l = local[part];
    const r = remote[part];
    if (readOnly) {
      actions[part] = r && r !== l ? 'adopt' : 'none';
    } else if (!profileExists) {
      actions[part] = l ? 'waiting' : 'none';
    } else if (pending[part] && l) {
      actions[part] = l === r ? 'none' : 'push';
    } else if (r) {
      actions[part] = r === l ? 'none' : 'adopt';
    } else {
      actions[part] = l ? 'push' : 'none';
    }
  }
  return actions;
}

// The request body for a save of the given parts, holding back any part over
// the limit so the rest can still be saved.
export function buildSave(leadId, local, parts) {
  const payload = { leadId };
  const oversized = [];
  for (const part of parts) {
    const value = local[part];
    if (value == null) continue;
    if (value.length > ASSESSMENT_LIMIT) {
      oversized.push(part);
      continue;
    }
    payload[PARTS[part].field] = value;
  }
  const sending = Object.keys(PARTS).filter(p => payload[PARTS[p].field] != null);
  return { payload, sending, oversized };
}

// The words shown beside the toolbar. Plain, and never alarming about work that
// is safe on the device.
export function describeSync(sync) {
  if (!sync) return null;
  switch (sync.state) {
    case 'saving':
      return { text: 'Saving to Salesforce...', tone: 'quiet' };
    case 'saved':
      return { text: 'Saved to Salesforce', tone: 'ok' };
    case 'waiting':
      return { text: 'Kept on this device until the questionnaire is saved', tone: 'warn' };
    case 'too-large': {
      const parts = (sync.parts || []).filter(p => PARTS[p]);
      if (!parts.length) return { text: 'Part of the assessment is too large to save to Salesforce, so it stays on this device', tone: 'warn' };
      const plural = parts.length > 1 || PARTS[parts[0]].plural;
      const names = parts.map((p, i) => (i === 0 ? PARTS[p].label : PARTS[p].label.toLowerCase()));
      const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
      return { text: `${list} ${plural ? 'are' : 'is'} too large to save to Salesforce, so ${plural ? 'they stay' : 'it stays'} on this device`, tone: 'warn' };
    }
    case 'error':
      return { text: 'Not saved to Salesforce yet. It is kept on this device and will be retried.', tone: 'warn' };
    case 'offline':
      return { text: 'Could not reach Salesforce, so this is the copy on this device', tone: 'warn' };
    default:
      return null;
  }
}
